import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { FiArrowLeft, FiDollarSign, FiCalendar, FiAlertCircle } from 'react-icons/fi';

interface FinancialAid {
  totalAwarded: number;
  totalAccepted: number;
  totalDisbursed: number;
  awards: Array<{
    name: string;
    type: 'grant' | 'loan' | 'scholarship' | 'work-study';
    amount: number;
    status: 'offered' | 'accepted' | 'disbursed' | 'declined';
    semester: string;
  }>;
  requirements: Array<{
    task: string;
    completed: boolean;
    dueDate?: string;
  }>;
}

const MyFinancialAidPage: React.FC = () => {
  const [financialAid, setFinancialAid] = useState<FinancialAid | null>(null);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFinancialAid = async () => {
      if (!currentUser) return;

      try {
        const docRef = doc(db, 'financialAid', currentUser.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setFinancialAid(docSnap.data() as FinancialAid);
        }
      } catch (error) {
        console.error('Error fetching financial aid:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFinancialAid();
  }, [currentUser]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'grant': return 'bg-green-100 text-green-800';
      case 'scholarship': return 'bg-blue-100 text-blue-800';
      case 'loan': return 'bg-yellow-100 text-yellow-800';
      case 'work-study': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'disbursed': return 'text-green-600';
      case 'accepted': return 'text-blue-600';
      case 'offered': return 'text-yellow-600';
      case 'declined': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center text-uc-navy hover:text-uc-gold transition-colors mb-4"
          >
            <FiArrowLeft size={20} className="mr-2" />
            Back to Home
          </button>
          <h1 className="text-3xl font-bold text-uc-navy font-header">My Financial Aid</h1>
          <p className="text-gray-600 mt-2">View your financial aid packages and status</p>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-uc-navy"></div>
            <p className="mt-2 text-gray-600">Loading financial aid information...</p>
          </div>
        ) : !financialAid ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FiDollarSign size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No financial aid information available</p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Awarded</p>
                    <p className="text-2xl font-bold text-uc-navy mt-1">
                      ${financialAid.totalAwarded.toLocaleString()}
                    </p>
                  </div>
                  <FiDollarSign size={32} className="text-uc-gold" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Accepted</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      ${financialAid.totalAccepted.toLocaleString()}
                    </p>
                  </div>
                  <FiDollarSign size={32} className="text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Disbursed</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      ${financialAid.totalDisbursed.toLocaleString()}
                    </p>
                  </div>
                  <FiDollarSign size={32} className="text-blue-500" />
                </div>
              </div>
            </div>

            {/* Awards */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold text-uc-navy mb-4 font-header">Aid Awards</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Award Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Semester</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Amount</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialAid.awards.map((award, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{award.name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getTypeColor(award.type)}`}>
                            {award.type.charAt(0).toUpperCase() + award.type.slice(1)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">{award.semester}</td>
                        <td className="px-4 py-3 text-right text-sm font-semibold">
                          ${award.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-semibold text-sm ${getStatusColor(award.status)}`}>
                            {award.status.charAt(0).toUpperCase() + award.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Requirements */}
            {financialAid.requirements && financialAid.requirements.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-uc-navy mb-4 font-header flex items-center">
                  <FiAlertCircle size={20} className="mr-2 text-yellow-600" />
                  Action Required
                </h2>
                <div className="space-y-3">
                  {financialAid.requirements.map((req, index) => (
                    <div
                      key={index}
                      className={`flex items-start p-4 rounded-lg ${
                        req.completed ? 'bg-green-50 border-l-4 border-green-500' : 'bg-yellow-50 border-l-4 border-yellow-500'
                      }`}
                    >
                      <div className="flex-1">
                        <p className={`font-medium ${req.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {req.task}
                        </p>
                        {req.dueDate && !req.completed && (
                          <p className="text-sm text-gray-600 mt-1 flex items-center">
                            <FiCalendar size={14} className="mr-1" />
                            Due: {new Date(req.dueDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {req.completed && (
                        <span className="ml-2 px-2 py-1 bg-green-200 text-green-800 text-xs font-semibold rounded">
                          Completed
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default MyFinancialAidPage;
